import * as THREE from "three";
import { ledSignTex } from "./signs";

export const TRAIN_Z = -9.05;

function plateTex(text, sub) {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 96;
  const g = c.getContext("2d");
  g.fillStyle = "#1a1d20";
  g.fillRect(0, 0, 256, 96);
  g.fillStyle = "#e8eef1";
  g.font = "bold 60px sans-serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(text, 128, 44);
  g.font = "20px sans-serif";
  g.fillText(sub, 128, 82);
  return new THREE.CanvasTexture(c);
}

function bulletTex() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d");
  g.beginPath();
  g.arc(64, 64, 58, 0, Math.PI * 2);
  g.fillStyle = "#6cbe45";
  g.fill();
  g.fillStyle = "#ffffff";
  g.font = "bold 78px sans-serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText("G", 64, 68);
  return new THREE.CanvasTexture(c);
}

export function buildTrain(scene, disposables, steelUrl = "/textures/steel.jpg") {
  const Z = TRAIN_Z;
  const loader = new THREE.TextureLoader();
  const steelTex = loader.load(steelUrl);
  steelTex.colorSpace = THREE.SRGBColorSpace;
  steelTex.wrapS = steelTex.wrapT = THREE.RepeatWrapping;
  steelTex.repeat.set(3, 1.5);
  const bodyMat = new THREE.MeshStandardMaterial({ map: steelTex, color: 0xb4bcc2, roughness: 0.38, metalness: 0.7 });
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x0a0d10, roughness: 0.8 });
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0xdcfff0,
    emissive: 0xc8ffe6,
    emissiveIntensity: 0.95,
    transparent: true,
    opacity: 0.92,
    roughness: 0.1,
  });
  const interiorMat = new THREE.MeshStandardMaterial({
    color: 0xcfd8d4,
    emissive: 0xd8ffee,
    emissiveIntensity: 0.45,
    side: THREE.BackSide,
  });
  const neonMat = new THREE.MeshStandardMaterial({ color: 0x001133, emissive: 0x2f7dff, emissiveIntensity: 2.6 });
  const greenMat = new THREE.MeshStandardMaterial({ color: 0x002211, emissive: 0x22e07a, emissiveIntensity: 2.2 });
  disposables.push(steelTex, bodyMat, darkMat, glassMat, interiorMat, neonMat, greenMat);

  const group = new THREE.Group();
  const add = (geo, mat, x, y, z) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    group.add(m);
    disposables.push(geo);
    return m;
  };

  // body segments (edges, window panels) + lintels above the three doors
  [[-3.2, 0.2], [3.2, 0.2], [-1.325, 1.15], [1.325, 1.15]].forEach(([x, w]) =>
    add(new THREE.BoxGeometry(w, 3.4, 0.14), bodyMat, x, 1.9, Z - 0.07)
  );
  [[-2.5, 1.2], [2.5, 1.2], [0, 1.5]].forEach(([x, w]) => add(new THREE.BoxGeometry(w, 1.35, 0.14), bodyMat, x, 2.925, Z - 0.07));
  add(new THREE.BoxGeometry(6.6, 0.18, 0.3), darkMat, 0, 3.68, Z - 0.12);
  // corrugated stainless ribs along the lower body (R160 look)
  const ribMat = new THREE.MeshStandardMaterial({ color: 0xd2d5d7, roughness: 0.3, metalness: 0.85 });
  disposables.push(ribMat);
  for (let y = 0.3; y < 1.15; y += 0.09) add(new THREE.BoxGeometry(6.56, 0.03, 0.02), ribMat, 0, y, Z + 0.01);
  for (let y = 2.75; y < 3.5; y += 0.09) add(new THREE.BoxGeometry(6.56, 0.03, 0.02), ribMat, 0, y, Z + 0.01);
  // black window band across the whole car
  add(new THREE.BoxGeometry(6.56, 1.1, 0.02), darkMat, 0, 1.78, Z + 0.006);
  add(new THREE.BoxGeometry(6.6, 0.4, 0.5), darkMat, 0, -0.2, Z - 0.75);

  // side windows with frames
  [-1.325, 1.325].forEach((x) => {
    [-0.26, 0.26].forEach((dx) => add(new THREE.PlaneGeometry(0.44, 0.86), glassMat, x + dx, 1.78, Z + 0.03));
  });
  [-3.2, 3.2].forEach((x) => add(new THREE.PlaneGeometry(0.14, 0.86), glassMat, x, 1.78, Z + 0.03));

  // route bullet + car number
  const bTex = bulletTex();
  const pTex = plateTex("8116", "GUMZ LINE");
  const bMat = new THREE.MeshStandardMaterial({ map: bTex, transparent: true, roughness: 0.5 });
  const pMat = new THREE.MeshStandardMaterial({ map: pTex, roughness: 0.5 });
  disposables.push(bTex, pTex, bMat, pMat);
  add(new THREE.PlaneGeometry(0.3, 0.3), bMat, -1.325, 2.5, Z + 0.02);
  add(new THREE.PlaneGeometry(0.42, 0.16), pMat, 1.325, 2.5, Z + 0.02);

  buildInterior(group, Z, add, disposables);

  // wheels
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0x1a1d20, roughness: 0.5, metalness: 0.7 });
  disposables.push(wheelMat);
  [-2.3, -1.5, 1.5, 2.3].forEach((x) => {
    const w = add(new THREE.CylinderGeometry(0.22, 0.22, 0.12, 16), wheelMat, x, -0.25, Z - 0.3);
    w.rotation.z = Math.PI / 2;
  });

  // LED destination sign + indicator lamps above the centre door
  const led = ledSignTex();
  const ledMat = new THREE.MeshStandardMaterial({ map: led, color: 0x000000, emissive: 0xffffff, emissiveMap: led, emissiveIntensity: 1.4 });
  disposables.push(led, ledMat);
  add(new THREE.PlaneGeometry(1.3, 0.16), ledMat, 0, 2.45, Z + 0.02);
  const lampMat = new THREE.MeshStandardMaterial({ color: 0x221100, emissive: 0xff9a1f, emissiveIntensity: 0.6 });
  const lampGeo = new THREE.SphereGeometry(0.035, 8, 8);
  disposables.push(lampMat, lampGeo);
  add(lampGeo, lampMat, -0.7, 2.7, Z + 0.03);
  add(lampGeo, lampMat, 0.7, 2.7, Z + 0.03);

  // door panels: closed side doors + the centre door that slides open
  const panelGeo = new THREE.BoxGeometry(0.75, 2.0, 0.06);
  const sideGeo = new THREE.BoxGeometry(0.6, 2.0, 0.06);
  const winGeo = new THREE.PlaneGeometry(0.36, 0.86);
  const sideWinGeo = new THREE.PlaneGeometry(0.3, 0.86);
  const edgeGeo = new THREE.BoxGeometry(0.03, 2.0, 0.07);
  disposables.push(panelGeo, sideGeo, winGeo, sideWinGeo, edgeGeo);
  const makePanel = (dir, cx, geo, wgeo, half) => {
    const p = new THREE.Group();
    p.add(new THREE.Mesh(geo, bodyMat));
    const win = new THREE.Mesh(wgeo, glassMat);
    win.position.set(0, 0.53, 0.035);
    p.add(win);
    const edge = new THREE.Mesh(edgeGeo, darkMat);
    edge.position.x = -dir * (half - 0.015);
    p.add(edge);
    p.position.set(cx + dir * half, 1.25, Z + 0.04);
    group.add(p);
    return p;
  };
  const panelL = makePanel(-1, 0, panelGeo, winGeo, 0.375);
  const panelR = makePanel(1, 0, panelGeo, winGeo, 0.375);
  [-2.5, 2.5].forEach((cx) => {
    makePanel(-1, cx, sideGeo, sideWinGeo, 0.3);
    makePanel(1, cx, sideGeo, sideWinGeo, 0.3);
  });

  // platform edge: white line + pink safety neon
  const edgeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.3 });
  disposables.push(edgeMat);
  add(new THREE.BoxGeometry(6.4, 0.01, 0.08), edgeMat, 0, 0.005, Z + 0.35);
  

  scene.add(group);

  return {
    setOpen(o) {
      panelL.position.x = -0.375 - o * 0.76;
      panelR.position.x = 0.375 + o * 0.76;
      lampMat.emissiveIntensity = o > 0.02 && o < 0.98 ? 2.8 : 0.6;
    },
  };
}


function speckleTex() {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const g = c.getContext("2d");
  g.fillStyle = "#3d4247";
  g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 2600; i++) {
    g.fillStyle = Math.random() > 0.5 ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.25)";
    g.fillRect(Math.random() * 256, Math.random() * 256, 1.5, 1.5);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(7, 1.4);
  return tex;
}

function adStripTex() {
  const c = document.createElement("canvas");
  c.width = 2048;
  c.height = 128;
  const g = c.getContext("2d");
  g.fillStyle = "#f2f2ee";
  g.fillRect(0, 0, 2048, 128);
  g.fillStyle = "#111";
  g.font = "bold 44px sans-serif";
  g.textBaseline = "middle";
  g.fillText("GUMZ LINE", 40, 64);
  g.strokeStyle = "#1a4fd0";
  g.lineWidth = 8;
  g.beginPath();
  g.moveTo(300, 64);
  g.lineTo(1400, 64);
  g.stroke();
  g.strokeStyle = "#6cbe45";
  g.beginPath();
  g.moveTo(1400, 64);
  g.lineTo(2000, 64);
  g.stroke();
  ["DEPOT", "GUMZ ST", "VOID GLASS", "SERPENT", "SIGNAL", "LIQUID", "BARCODE"].forEach((n, i) => {
    const x = 320 + i * 275;
    g.fillStyle = "#fff";
    g.beginPath();
    g.arc(x, 64, 12, 0, Math.PI * 2);
    g.fill();
    g.lineWidth = 4;
    g.strokeStyle = "#111";
    g.stroke();
    g.fillStyle = "#111";
    g.font = "600 20px sans-serif";
    g.fillText(n, x - 30, 100);
  });
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// A full walk-in car: floor, ceiling tubes, bench seats both sides, poles, grab bars with straps, ads, end doors
function buildInterior(group, Z, add, disposables) {
  const L = 14; // car length along x
  const zFront = Z - 0.15; // inside face of the platform-side wall
  const zBack = Z - 2.7;
  const zc = (zFront + zBack) / 2;
  const floorY = 0.22;
  const ceilY = 2.5;

  const floorTex = speckleTex();
  const floorMat = new THREE.MeshStandardMaterial({ map: floorTex, roughness: 0.85 });
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xc4c8cb, roughness: 0.55, metalness: 0.25 });
  const ceilMat = new THREE.MeshStandardMaterial({ color: 0xd9dcde, roughness: 0.6 });
  const seatMat = new THREE.MeshStandardMaterial({ color: 0x4f5e73, roughness: 0.8 });
  const chrome = new THREE.MeshStandardMaterial({ color: 0xd9dee2, roughness: 0.18, metalness: 0.95 });
  const glassMat = new THREE.MeshStandardMaterial({ color: 0x0a0d10, roughness: 0.05, metalness: 0.6, emissive: 0x0b1420, emissiveIntensity: 0.6 });
  const tubeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xf4f7f8, emissiveIntensity: 2.4 });
  const strapMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.9 });
  const adTex = adStripTex();
  const adMat = new THREE.MeshStandardMaterial({ map: adTex, emissive: 0xffffff, emissiveMap: adTex, emissiveIntensity: 0.18, roughness: 0.6 });
  disposables.push(floorTex, floorMat, wallMat, ceilMat, seatMat, chrome, glassMat, tubeMat, strapMat, adTex, adMat);

  // shell
  add(new THREE.BoxGeometry(L, 0.04, zFront - zBack), floorMat, 0, floorY, zc);
  add(new THREE.BoxGeometry(L, 0.04, zFront - zBack), ceilMat, 0, ceilY, zc);
  add(new THREE.BoxGeometry(L, ceilY - floorY, 0.04), wallMat, 0, (ceilY + floorY) / 2, zBack); // far wall
  add(new THREE.BoxGeometry(0.04, ceilY - floorY, zFront - zBack), wallMat, -L / 2, (ceilY + floorY) / 2, zc);
  add(new THREE.BoxGeometry(0.04, ceilY - floorY, zFront - zBack), wallMat, L / 2, (ceilY + floorY) / 2, zc);
  // platform-side wall pieces (leave the centre door open)
  [-1, 1].forEach((s) => add(new THREE.BoxGeometry(L / 2 - 0.75, ceilY - floorY, 0.04), wallMat, s * (L / 4 + 0.375), (ceilY + floorY) / 2, zFront));

  // windows along both long walls (dark tunnel outside)
  for (let x = -6; x <= 6; x += 1.5) {
    add(new THREE.PlaneGeometry(1.1, 0.8), glassMat, x, 1.75, zBack + 0.03);
    if (Math.abs(x) > 1.2) add(new THREE.PlaneGeometry(1.1, 0.8), glassMat, x, 1.75, zFront - 0.03).rotation.y = Math.PI;
  }
  // ad strip / route map above the windows
  add(new THREE.PlaneGeometry(L - 0.4, 0.34), adMat, 0, 2.3, zBack + 0.03);
  [-1, 1].forEach((s) => add(new THREE.PlaneGeometry(L / 2 - 1.0, 0.34), adMat, s * (L / 4 + 0.5), 2.3, zFront - 0.03).rotation.y = Math.PI);

  // bench seats: continuous along the far wall, short benches between doors on the near wall
  const bench = (x, w, z, facing) => {
    add(new THREE.BoxGeometry(w, 0.08, 0.46), seatMat, x, 0.66, z + facing * 0.25);
    const back = add(new THREE.BoxGeometry(w, 0.55, 0.06), seatMat, x, 0.98, z + facing * 0.03);
    back.rotation.x = -facing * 0.12;
    add(new THREE.BoxGeometry(w, 0.44, 0.06), wallMat, x, 0.44, z + facing * 0.06); // kick panel
    for (let i = 0; i <= Math.round(w / 0.55); i++) {
      const dx = -w / 2 + i * (w / Math.round(w / 0.55));
      add(new THREE.BoxGeometry(0.03, 0.5, 0.42), chrome, x + dx, 0.95, z + facing * 0.24);
    }
  };
  bench(-3.6, 6.2, zBack + 0.04, 1);
  bench(3.6, 6.2, zBack + 0.04, 1);
  bench(-4.2, 4.8, zFront - 0.04, -1);
  bench(4.2, 4.8, zFront - 0.04, -1);

  // vertical poles + ceiling grab bars with hanging straps
  [-1.15, 1.15, -4.3, 4.3, -6.4, 6.4].forEach((x) => add(new THREE.CylinderGeometry(0.022, 0.022, ceilY - floorY, 10), chrome, x, (ceilY + floorY) / 2, zc));
  [zc - 0.55, zc + 0.55].forEach((z) => {
    add(new THREE.CylinderGeometry(0.016, 0.016, L - 0.6, 8), chrome, 0, ceilY - 0.32, z).rotation.z = Math.PI / 2;
    for (let x = -6.3; x <= 6.3; x += 0.7) {
      const strap = add(new THREE.TorusGeometry(0.07, 0.012, 6, 16), strapMat, x, ceilY - 0.5, z);
      strap.rotation.y = Math.PI / 2;
      add(new THREE.CylinderGeometry(0.006, 0.006, 0.12, 6), strapMat, x, ceilY - 0.4, z);
    }
  });

  // fluorescent strips + lights
  [-0.55, 0.55].forEach((dz) => add(new THREE.BoxGeometry(L - 0.8, 0.035, 0.12), tubeMat, 0, ceilY - 0.03, zc + dz));
  for (let x = -4.5; x <= 4.5; x += 4.5) {
    const pl = new THREE.PointLight(0xe9eef0, 7, 6, 2);
    pl.position.set(x, ceilY - 0.25, zc);
    group.add(pl);
  }

  // end doors to the next cars
  [-1, 1].forEach((s) => {
    add(new THREE.BoxGeometry(0.06, 1.95, 0.9), wallMat, s * (L / 2 - 0.05), floorY + 0.98, zc);
    add(new THREE.PlaneGeometry(0.5, 0.7), glassMat, s * (L / 2 - 0.09), 1.75, zc).rotation.y = -s * Math.PI / 2;
  });
}
